import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Image, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, type WikiArticle } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { WikiMarkdown } from '@/components/wiki/WikiMarkdown';
import { WikiTextToolbar } from '@/components/wiki/WikiTextToolbar';
import {
  createWikiImageBlock,
  createWikiTextBlock,
  hasWikiContent,
  isWikiImageUrl,
  parseWikiContent,
  serializeWikiContent,
  type WikiContentBlock,
} from '@/lib/wiki-content';

type Form = {
  id?: number;
  slug: string;
  title: string;
  summary: string;
  imageUrl: string;
  blocks: WikiContentBlock[];
  sortOrder: number;
  isVisible: boolean;
};

const empty = (): Form => ({
  slug: '',
  title: '',
  summary: '',
  imageUrl: '',
  blocks: [createWikiTextBlock()],
  sortOrder: 0,
  isVisible: true,
});

export function AdminWiki() {
  const queryClient = useQueryClient();
  const textareas = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const { data } = useQuery({ queryKey: ['admin', 'wiki'], queryFn: adminApi.wiki.list });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(empty());

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'wiki'] });
    queryClient.invalidateQueries({ queryKey: ['wiki'] });
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        summary: form.summary.trim(),
        imageUrl: form.imageUrl.trim() || null,
        content: serializeWikiContent(form.blocks),
        sortOrder: form.sortOrder,
        isVisible: form.isVisible,
      };
      return form.id
        ? adminApi.wiki.update(form.id, payload)
        : adminApi.wiki.create(payload);
    },
    onSuccess: () => {
      toast.success('Статья сохранена');
      invalidate();
      setOpen(false);
    },
    onError: () => toast.error('Не удалось сохранить статью'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminApi.wiki.remove(id),
    onSuccess: () => {
      toast.success('Статья удалена');
      invalidate();
    },
    onError: () => toast.error('Не удалось удалить статью'),
  });

  function openEdit(article: WikiArticle) {
    setForm({
      id: article.id,
      slug: article.slug,
      title: article.title,
      summary: article.summary,
      imageUrl: article.imageUrl ?? '',
      blocks: parseWikiContent(article.content),
      sortOrder: article.sortOrder ?? 0,
      isVisible: article.isVisible ?? true,
    });
    setOpen(true);
  }

  const canSave =
    form.slug.trim().length >= 2 &&
    /^[a-z0-9-]+$/.test(form.slug.trim()) &&
    form.title.trim().length > 0 &&
    form.summary.trim().length > 0 &&
    hasWikiContent(form.blocks) &&
    form.blocks.every((block) => block.type === 'text' || isWikiImageUrl(block.url));

  function insertBlock(afterIndex: number, type: WikiContentBlock['type']) {
    const blocks = [...form.blocks];
    blocks.splice(
      afterIndex + 1,
      0,
      type === 'text' ? createWikiTextBlock() : createWikiImageBlock(),
    );
    setForm({ ...form, blocks });
  }

  function updateTextBlock(id: string, text: string) {
    setForm({
      ...form,
      blocks: form.blocks.map((block) =>
        block.id === id && block.type === 'text' ? { ...block, text } : block,
      ),
    });
  }

  function updateImageBlock(id: string, patch: { url?: string; caption?: string }) {
    setForm({
      ...form,
      blocks: form.blocks.map((block) =>
        block.id === id && block.type === 'image' ? { ...block, ...patch } : block,
      ),
    });
  }

  function moveBlock(index: number, direction: -1 | 1) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= form.blocks.length) return;
    const blocks = [...form.blocks];
    [blocks[index], blocks[targetIndex]] = [blocks[targetIndex]!, blocks[index]!];
    setForm({ ...form, blocks });
  }

  function removeBlock(id: string) {
    const blocks = form.blocks.filter((block) => block.id !== id);
    setForm({ ...form, blocks: blocks.length > 0 ? blocks : [createWikiTextBlock()] });
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-eol-white">Вики</h1>
          <p className="mt-1 text-sm text-eol-desc">Карточки и содержимое статей сервера.</p>
        </div>
        <Button onClick={() => { setForm(empty()); setOpen(true); }} className="gap-2">
          <Plus className="h-4 w-4" /> Добавить статью
        </Button>
      </div>

      {!data?.length ? (
        <p className="text-eol-desc">Пока пусто.</p>
      ) : (
        <div className="space-y-3">
          {data.map((article) => (
            <Card key={article.id}>
              <CardContent className="flex flex-wrap items-start gap-4 py-4">
                {article.imageUrl ? (
                  <img
                    src={article.imageUrl}
                    alt=""
                    className="h-20 w-32 rounded-lg border border-white/5 object-cover"
                  />
                ) : (
                  <div className="flex h-20 w-32 items-center justify-center rounded-lg bg-white/5">
                    <Image className="h-6 w-6 text-eol-desc" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-bold text-eol-white">{article.title}</p>
                    {!article.isVisible && <Badge variant="danger">Скрыта</Badge>}
                  </div>
                  <p className="mt-1 text-xs font-semibold text-eol-pink">/wiki/{article.slug}</p>
                  <p className="mt-2 line-clamp-2 text-sm text-eol-desc">{article.summary}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="secondary" onClick={() => openEdit(article)} title="Редактировать">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      if (confirm(`Удалить статью «${article.title}»?`)) remove.mutate(article.id);
                    }}
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Редактировать статью' : 'Новая статья'}</DialogTitle>
            <DialogDescription>
              Настройте карточку, изображение и основной текст статьи.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-eol-desc">Название</span>
                <Input
                  className="mt-1"
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                  placeholder="Начало игры"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-eol-desc">Адрес статьи</span>
                <Input
                  className="mt-1"
                  value={form.slug}
                  onChange={(event) => setForm({ ...form, slug: event.target.value.toLowerCase() })}
                  placeholder="getting-started"
                />
                <span className="mt-1 block text-xs text-eol-desc">Латиница, цифры и дефис.</span>
              </label>
            </div>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-eol-desc">Описание карточки</span>
              <textarea
                rows={3}
                className="mt-1 w-full rounded-md border border-white/10 bg-eol-black/40 px-3 py-2 text-sm text-eol-white"
                value={form.summary}
                onChange={(event) => setForm({ ...form, summary: event.target.value })}
                placeholder="Коротко расскажите, о чём эта статья."
              />
            </label>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-eol-desc">Картинка</span>
              <Input
                className="mt-1"
                value={form.imageUrl}
                onChange={(event) => setForm({ ...form, imageUrl: event.target.value })}
                placeholder="https://… или /images/wiki/example.webp"
              />
            </label>

            {form.imageUrl && (
              <img
                src={form.imageUrl}
                alt="Предпросмотр"
                className="aspect-[16/6] w-full rounded-lg border border-white/5 object-cover"
              />
            )}

            <div>
              <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-eol-desc">
                    Содержимое статьи
                  </h3>
                  <p className="mt-1 text-xs text-eol-desc">
                    Добавляйте текст и изображения в нужном порядке.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => insertBlock(form.blocks.length - 1, 'text')}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" /> Текст
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => insertBlock(form.blocks.length - 1, 'image')}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" /> Картинка
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {form.blocks.map((block, index) => (
                  <div key={block.id} className="rounded-lg border border-white/10 bg-eol-black/25 p-3">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-eol-pink">
                        {block.type === 'text' ? 'Текст' : 'Изображение'} · {index + 1}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          size="icon"
                          variant="secondary"
                          className="h-8 w-8"
                          disabled={index === 0}
                          onClick={() => moveBlock(index, -1)}
                          aria-label={`Переместить блок ${index + 1} вверх`}
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="secondary"
                          className="h-8 w-8"
                          disabled={index === form.blocks.length - 1}
                          onClick={() => moveBlock(index, 1)}
                          aria-label={`Переместить блок ${index + 1} вниз`}
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          onClick={() => removeBlock(block.id)}
                          aria-label={`Удалить блок ${index + 1}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    {block.type === 'text' ? (
                      <div>
                        <WikiTextToolbar
                          value={block.text}
                          onChange={(value) => updateTextBlock(block.id, value)}
                          getTextarea={() => textareas.current[block.id] ?? null}
                        />
                        <textarea
                          ref={(element) => { textareas.current[block.id] = element; }}
                          rows={6}
                          aria-label={`Текст блока ${index + 1}`}
                          className="w-full rounded-md border border-white/10 bg-eol-black/40 px-3 py-2 text-sm leading-6 text-eol-white"
                          value={block.text}
                          onChange={(event) => updateTextBlock(block.id, event.target.value)}
                          placeholder="Введите текст статьи…"
                        />
                        {block.text.trim() && (
                          <div className="mt-3 rounded-md border border-white/10 bg-eol-bg/70 px-4 py-3">
                            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-eol-desc">
                              Предпросмотр
                            </p>
                            <WikiMarkdown content={block.text} />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Input
                          aria-label={`Ссылка изображения блока ${index + 1}`}
                          value={block.url}
                          onChange={(event) => updateImageBlock(block.id, { url: event.target.value })}
                          placeholder="https://… или /images/wiki/example.webp"
                        />
                        <Input
                          aria-label={`Подпись изображения блока ${index + 1}`}
                          value={block.caption}
                          onChange={(event) => updateImageBlock(block.id, { caption: event.target.value })}
                          placeholder="Подпись под изображением (необязательно)"
                        />
                        {isWikiImageUrl(block.url) && (
                          <img
                            src={block.url}
                            alt="Предпросмотр блока"
                            className="max-h-72 w-full rounded-lg border border-white/5 object-cover"
                          />
                        )}
                        {block.url.trim() && !isWikiImageUrl(block.url) && (
                          <p className="text-xs text-eol-red">
                            Укажите ссылку http(s) или путь, начинающийся с /.
                          </p>
                        )}
                      </div>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2 border-t border-white/5 pt-3">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => insertBlock(index, 'text')}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" /> Текст после
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => insertBlock(index, 'image')}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" /> Картинка после
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wider text-eol-desc">Порядок</span>
                <NumberInput
                  value={form.sortOrder}
                  onChange={(value) => setForm({ ...form, sortOrder: value })}
                  step={10}
                  className="w-28"
                />
              </label>
              <label className="mt-5 flex items-center gap-2 text-eol-white">
                <input
                  type="checkbox"
                  checked={form.isVisible}
                  onChange={(event) => setForm({ ...form, isVisible: event.target.checked })}
                />
                Показывать на сайте
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Отмена</Button>
            <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
